/**
 * Framing screens for the SHSAT Adaptive test flow.
 *
 * Mirrors the legacy ``Module/index.tsx`` start/prompt/end markup
 * (lines 2089-2103, 2435-2439, 2442-2449) byte-for-byte. The CSS
 * (``digitalSHSATModule__startContainer``, ``__promptContainer``,
 * ``__endContainer``) is inherited from ``Module/index.css`` — no new
 * styling is added.
 *
 * The wrappers around each screen include the ``digitalSHSAT`` →
 * ``digitalSHSAT__box`` → ``digitalSHSATModule`` chain so the framing
 * pages share the same width/centering/background as the legacy flow.
 *
 * No tools, no header arrows, no progress bar on these screens — just
 * the centered card content. The header (``AdaptiveTopBar``) mounts
 * inside the in-module phase only.
 */

import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { TfiTimer } from 'react-icons/tfi';
import { IoMdPerson } from 'react-icons/io';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import renderMathInElement from 'katex/dist/contrib/auto-render.js';
import 'katex/dist/katex.min.css';

import { AdaptiveTopBar } from './AdaptiveTopBar';
import { SectionEndScreen } from '../SectionEndScreen';
import { sectionEndTitle } from '../sectionOrder';
import { SectionStartPicker } from '../SectionStartPicker';
import { StartSection } from '../sectionOrder';


const KATEX_DELIMITERS = [
  { left: '$$', right: '$$', display: true },
  { left: '$', right: '$', display: false },
  { left: '\\(', right: '\\)', display: false },
  { left: '\\[', right: '\\]', display: true },
];


function formatDuration(totalSeconds: number): string {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  // Match the legacy Test Start wording (``formatTime2``): "2 hours 30 mins"
  // / "2 hours". SHSAT tests are multi-hour, so the hours form is canonical.
  return mins > 0 ? `${hrs} hours ${mins} mins` : `${hrs} hours`;
}


/** Minimal top header used on the framing screens.
 *
 * Mirrors the legacy SHSAT header in shape (arrows + timer + user) but
 * omits the in-module-only function-group (Pointer/Eliminator/Notepad)
 * because tools only become visible once the student is on a question
 * — matches legacy (Module/index.tsx:1944, function group is gated on
 * a matching current question).
 *
 * When ``onAdvance`` is provided, the forward arrow doubles as the
 * "Start"/"Continue" CTA so the framing screens don't need a separate
 * button under the card. Back arrow is always disabled — no-skip rule.
 *
 * The bottom strip shows TEST NAME / SECTION NAME (matches the prod
 * screenshot for the directions screen). */
function FrameHeader(props: {
  testName: string | null;
  sectionName?: string | null;
  studentName?: string | null;
  onAdvance?: () => void;
}): JSX.Element {
  const { testName, sectionName, studentName, onAdvance } = props;
  return (
    <div className="digitalSHSATModule__header">
      <div className="digitalSHSATModule__headerTop">
        <div className="digitalSHSATModule__headerBox">
          <div className="digitalSHSATModule__headerLeft">
            <div className="digitalSHSATModule__arrowButtons">
              <div className="digitalSHSATModule__buttonWithTooltip">
                <button
                  type="button"
                  className="digitalSHSATModule__arrowButton"
                  disabled
                  aria-label="Previous (disabled — adaptive does not allow back)"
                >
                  <FaArrowLeft />
                </button>
              </div>
              <div className="digitalSHSATModule__buttonWithTooltip">
                <button
                  type="button"
                  className="digitalSHSATModule__arrowButton"
                  disabled={!onAdvance}
                  onClick={onAdvance}
                  aria-label="Next"
                >
                  <FaArrowRight />
                </button>
                {onAdvance && (
                  <span className="digitalSHSATModule__tooltip">Next</span>
                )}
              </div>
            </div>
          </div>
          <div className="digitalSHSATModule__headerRight">
            <div className="digitalSHSATModule__timer">
              <button type="button" className="digitalSHSATModule__timerButton">
                <TfiTimer />
              </button>
            </div>
            {studentName && (
              <div className="digitalSHSATModule__guest">{studentName}</div>
            )}
            <div className="digitalSHSATModule__guestButton">
              <IoMdPerson />
            </div>
          </div>
        </div>
      </div>
      <div className="digitalSHSATModule__headerBoundary" />
      <div className="digitalSHSATModule__headerBottom">
        <div className="digitalSHSATModule__headerBottomContents">
          <div>
            {(testName || 'DIGITAL SHSAT PRACTICE TEST').toUpperCase()}
            {'  '}{'  '}/{'  '}{'  '}
          </div>
          {sectionName && (
            <div>{sectionName.toUpperCase()}{'  '}{'  '}/{'  '}{'  '}</div>
          )}
        </div>
      </div>
    </div>
  );
}


function FrameShell(props: {
  testName: string | null;
  sectionName?: string | null;
  studentName?: string | null;
  onAdvance?: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="digitalSHSAT" onCopy={(e) => e.preventDefault()}>
      <Helmet>
        <title>{props.testName || 'Digital SHSAT Practice Test'}</title>
      </Helmet>
      <div className="digitalSHSAT__box">
        <div id="digitalSHSATModule" className="digitalSHSATModule">
          <FrameHeader
            testName={props.testName}
            sectionName={props.sectionName}
            studentName={props.studentName}
            onAdvance={props.onAdvance}
          />
          <div id="digitalSHSATModule__body" className="digitalSHSATModule__body">
            {props.children}
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Test Start (Image #17)
// ---------------------------------------------------------------------------


export function AdaptiveTestStart(props: {
  testName: string | null;
  studentName?: string | null;
  totalQuestions: number;
  totalDurationSeconds: number | null;
  onStart: () => void;
  /** "Start with ELA or Math first" picker (Part A). Only rendered when
   * ``showSectionChoice`` — i.e. the test actually has both sections.
   * The choice is applied by the parent's ``onStart`` (module reorder);
   * it is never persisted. */
  startSection?: StartSection;
  onStartSectionChange?: (value: StartSection) => void;
  showSectionChoice?: boolean;
}): JSX.Element {
  const {
    testName,
    studentName,
    totalQuestions,
    totalDurationSeconds,
    onStart,
    startSection,
    onStartSectionChange,
    showSectionChoice,
  } = props;
  const durationLabel = totalDurationSeconds == null
    ? 'Untimed'
    : formatDuration(totalDurationSeconds);
  return (
    <FrameShell testName={testName} studentName={studentName}>
      <div className="digitalSHSATModule__startContainer">
        <div className="digitalSHSATModule__startInnerContainer">
          <div className="digitalSHSATModule__startInnerTop">
            <div className="digitalSHSATModule__startInnerTopHeader">Test Start</div>
            <div className="digitalSHSATModule__startInnerTopInfoBox">
              <div className="digitalSHSATModule__startInnerTopInfo">{totalQuestions} Questions</div>
              <div className="digitalSHSATModule__startInnerTopInfo">{durationLabel}</div>
            </div>
          </div>
          <div className="digitalSHSATModule__startInnerBottom">
            {showSectionChoice && startSection && onStartSectionChange && (
              <SectionStartPicker value={startSection} onChange={onStartSectionChange} />
            )}
            <div className="digitalSHSATModule__startInnerBottomInfo">
              When you click the Start button, your time will begin counting down.
            </div>
            <div
              className="digitalSHSATModule__startInnerBottomButton"
              onClick={onStart}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onStart(); }}
            >
              Start
            </div>
          </div>
        </div>
      </div>
    </FrameShell>
  );
}


// ---------------------------------------------------------------------------
// Directions (Image #18, #20)
// ---------------------------------------------------------------------------


export function AdaptiveDirections(props: {
  testName: string | null;
  sectionName?: string | null;
  studentName?: string | null;
  prompt: string;
  onContinue: () => void;
}): JSX.Element {
  const { testName, sectionName, studentName, prompt, onContinue } = props;
  const promptRef = useRef<HTMLSpanElement>(null);

  // The prompt field stores KaTeX-mixed text (e.g.
  // ``$$\textbf{English Language Arts}$$``). Legacy relies on a page-
  // wide ``renderMathInElement`` sweep; adaptive renders the prompt in
  // isolation, so trigger KaTeX explicitly here.
  useEffect(() => {
    if (!promptRef.current) return;
    renderMathInElement(promptRef.current, {
      delimiters: KATEX_DELIMITERS,
      trust: true,
    });
  }, [prompt]);

  return (
    <FrameShell
      testName={testName}
      sectionName={sectionName}
      studentName={studentName}
      onAdvance={onContinue}
    >
      <div className="digitalSHSATModule__promptContainer">
        <div className="digitalSHSATModule__promptBox">
          <span ref={promptRef} className="katex">{prompt}</span>
        </div>
      </div>
    </FrameShell>
  );
}


// ---------------------------------------------------------------------------
// End of Test (Image #19)
// ---------------------------------------------------------------------------


export function AdaptiveEndOfTest(props: {
  testName: string | null;
  sectionName?: string | null;
  studentName?: string | null;
  /** True when this (ELA) module actually has a "Stand alone items" block, so
   * the title reads "End of ELA – Stand alone items"; false → "End of ELA" /
   * "End of Math". Mirrors legacy ``isElaSection && standaloneBlocks.length>0``. */
  endsOnStandalone?: boolean;
  onSubmit: () => void;
  // Top-bar context so the submit screen wears the SAME full header as
  // the legacy submit screen (toolbar + timer + progress + test/section
  // strip), rather than the minimal framing header.
  studentTestId: string;
  moduleId: string;
  durationSeconds?: number | null;
  totalSlots?: number;
  /** "Discard and Exit" from the pre-submit screen (data-destructive). */
  onDiscardExit?: () => void;
}): JSX.Element {
  const {
    testName,
    sectionName,
    studentName,
    endsOnStandalone = false,
    onSubmit,
    studentTestId,
    moduleId,
    durationSeconds,
    totalSlots = 0,
    onDiscardExit,
  } = props;
  return (
    <div className="digitalSHSAT" onCopy={(e) => e.preventDefault()}>
      <Helmet>
        <title>{testName || 'Digital SHSAT Practice Test'}</title>
      </Helmet>
      <div className="digitalSHSAT__box">
        <div id="digitalSHSATModule" className="digitalSHSATModule">
          {/* Full test header (arrows disabled, no Review, no current
              slot) so the submit screen matches the legacy one. */}
          <AdaptiveTopBar
            testName={testName}
            sectionName={sectionName ?? null}
            durationSeconds={durationSeconds ?? null}
            studentTestId={studentTestId}
            moduleId={moduleId}
            slotNumber={totalSlots || null}
            totalSlots={totalSlots}
            studentName={studentName ?? null}
            canAdvance={false}
            canGoBack={false}
            onDiscardExit={onDiscardExit}
          />
          <div id="digitalSHSATModule__body" className="digitalSHSATModule__body">
            {/* Per-section end screen: "End of ELA – Stand alone items" /
                "End of Math" (ELA ends on the stand-alone block). Reuses the
                shared end-screen card; the button triggers the existing
                final submit. */}
            <SectionEndScreen
              title={sectionEndTitle(sectionName ?? null, endsOnStandalone)}
              allAnswered={true}
              buttonLabel="Submit Final Answers"
              onButton={onSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// End of Section (non-final section boundary)
// ---------------------------------------------------------------------------


/** Shown when a NON-final section finishes (before the next section's
 * directions). "End of ELA – Stand alone items" / "End of Math"; the
 * Continue button advances the parent to the next module. Uses the minimal
 * framing header (like AdaptiveDirections), since it sits between modules. */
export function AdaptiveSectionEnd(props: {
  testName: string | null;
  sectionName?: string | null;
  studentName?: string | null;
  /** True when this (ELA) module actually has a "Stand alone items" block —
   * see AdaptiveEndOfTest. false → "End of ELA" / "End of Math". */
  endsOnStandalone?: boolean;
  onContinue: () => void;
}): JSX.Element {
  const { testName, sectionName, studentName, endsOnStandalone = false, onContinue } = props;
  return (
    <FrameShell
      testName={testName}
      sectionName={sectionName ?? null}
      studentName={studentName}
    >
      <SectionEndScreen
        title={sectionEndTitle(sectionName ?? null, endsOnStandalone)}
        allAnswered={true}
        buttonLabel="Continue"
        onButton={onContinue}
      />
    </FrameShell>
  );
}
